import React, { useMemo, useState } from 'react';
import { Users, GraduationCap, Heart, Shield, Plus, Eye, Edit2, RotateCcw, PowerOff, Search, Link as LinkIcon, X } from 'lucide-react';
import { PageLayout } from '../shared/PageLayout';
import { StatCard } from '../shared/StatCard';
import { DataTable } from '../shared/DataTable';
import { SlideOver } from '../shared/SlideOver';
import { Badge } from '../../../ui/Badge';
import { Button } from '../../../ui/Button';
import { useAppStore } from '../../../../hooks/useAppStore';
import { AccountsState, isLearnerAccount, isTeacherAccount, isParentAccount, isAdminAccount, Account } from '../../../../types/accounts';
import { AppState, ModuleId } from '../../../../types';
import { Avatar } from '../shared/Avatar';
import { formatLastActive } from '../shared/utils';
interface UserManagementPageProps {
  accounts?: AccountsState;
  getLearnerData?: (learnerId: string) => AppState | null;
  getLearnerModuleMastery?: (learnerId: string, moduleId: ModuleId) => number;
  onNavigateToLearner?: (learnerId: string) => void;
}
interface UserRow {
  id: string;
  name: string;
  email: string;
  role: 'learner' | 'teacher' | 'parent' | 'admin';
  organization: string;
  linkedCount: number;
  xp: number;
  lastActive: string;
  status: 'active' | 'inactive';
  account?: Account;
}
const ROLE_CONFIG = {
  learner: {
    label: 'Leerling',
    variant: 'info' as const,
    icon: Users
  },
  teacher: {
    label: 'Docent',
    variant: 'secondary' as const,
    icon: GraduationCap
  },
  parent: {
    label: 'Ouder',
    variant: 'success' as const,
    icon: Heart
  },
  admin: {
    label: 'Admin',
    variant: 'warning' as const,
    icon: Shield
  }
};
export function UserManagementPage({
  accounts: propsAccounts,
  getLearnerData: propsGetLearnerData,
  getLearnerModuleMastery,
  onNavigateToLearner
}: UserManagementPageProps) {
  // Use store directly to get mutation functions
  const store = useAppStore();
  const accounts = propsAccounts || store.accounts;
  const getLearnerData = propsGetLearnerData || store.getLearnerData;
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [roleFilter, setRoleFilter] = useState<'all' | 'learner' | 'teacher' | 'parent' | 'admin'>('all');
  const [resetConfirm, setResetConfirm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  // Modals state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  // Add User Form State
  const [newUserRole, setNewUserRole] = useState<'learner' | 'teacher' | 'parent' | 'admin'>('learner');
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserSchool, setNewUserSchool] = useState('');
  const [newUserClass, setNewUserClass] = useState('');
  const [newUserGrade, setNewUserGrade] = useState('6');
  // Edit User Form State
  const [editUserName, setEditUserName] = useState('');
  const [editUserEmail, setEditUserEmail] = useState('');
  const [editUserRole, setEditUserRole] = useState<'learner' | 'teacher' | 'parent' | 'admin'>('learner');
  const [editUserOrganization, setEditUserOrganization] = useState('');
  // Link User Form State
  const [linkSourceId, setLinkSourceId] = useState('');
  const [linkTargetId, setLinkTargetId] = useState('');
  const users = useMemo((): UserRow[] => {
    if (!accounts || !accounts.accountsById) return [];
    const allAccounts = Object.values(accounts.accountsById);
    return allAccounts.map((account): UserRow => {
      let organization = '—';
      let linkedCount = 0;
      let xp = 0;
      if (isTeacherAccount(account)) {
        organization = account.schoolName || '—';
        linkedCount = (accounts.relationships.teacherToLearners[account.id] || []).length;
      } else if (isParentAccount(account)) {
        linkedCount = (accounts.relationships.parentToLearners[account.id] || []).length;
      } else if (isLearnerAccount(account)) {
        const teacherId = Object.entries(accounts.relationships.teacherToLearners || {}).find(([_, ids]) => ids.includes(account.id))?.[0];
        const teacher = teacherId ? accounts.accountsById[teacherId] : null;
        organization = teacher && isTeacherAccount(teacher) ? teacher.schoolName || '—' : '—';
        const parentId = Object.entries(accounts.relationships.parentToLearners || {}).find(([_, ids]) => ids.includes(account.id))?.[0];
        linkedCount = parentId ? 1 : 0;
        if (getLearnerData) {
          const data = getLearnerData(account.id);
          xp = data?.stats?.xp ?? 0;
        }
      } else if (isAdminAccount(account)) {
        organization = 'Meester djay';
      }
      return {
        id: account.id,
        name: account.displayName,
        email: account.email || '—',
        role: account.role,
        organization,
        linkedCount,
        xp,
        lastActive: formatLastActive(account.lastActive),
        status: 'active',
        account
      };
    });
  }, [accounts, getLearnerData]);
  const filtered = useMemo(() => {
    if (roleFilter === 'all') return users;
    return users.filter(u => u.role === roleFilter);
  }, [users, roleFilter]);
  const counts = useMemo(() => ({
    learner: users.filter(u => u.role === 'learner').length,
    teacher: users.filter(u => u.role === 'teacher').length,
    parent: users.filter(u => u.role === 'parent').length,
    admin: users.filter(u => u.role === 'admin').length
  }), [users]);
  const handleAddUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserName.trim()) return;
    store.createAccount(newUserRole, {
      displayName: newUserName,
      email: newUserEmail,
      schoolName: newUserSchool,
      classroomName: newUserClass,
      grade: parseInt(newUserGrade, 10)
    });
    setIsAddModalOpen(false);
    setNewUserName('');
    setNewUserEmail('');
    setNewUserSchool('');
    setNewUserClass('');
  };
  const handleLinkUsers = (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkSourceId || !linkTargetId) return;
    const sourceAccount = accounts?.accountsById?.[linkSourceId];
    if (sourceAccount?.role === 'parent') {
      store.linkParentToLearnerById(linkSourceId, linkTargetId);
    } else if (sourceAccount?.role === 'teacher') {
      store.linkTeacherToLearnerById(linkSourceId, linkTargetId);
    }
    setIsLinkModalOpen(false);
    setLinkSourceId('');
    setLinkTargetId('');
  };
  const handleEditUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser || !editUserName.trim()) return;
    // Update display name
    if (editUserName !== selectedUser.name) {
      store.updateAccountNameById(selectedUser.id, editUserName.trim());
    }
    // Update email
    if (editUserEmail !== (selectedUser.email !== '—' ? selectedUser.email : '')) {
      store.updateAccountEmail(selectedUser.id, editUserEmail.trim());
    }
    // Update teacher/learner organization (school name)
    if ((editUserRole === 'teacher' || editUserRole === 'learner') && editUserOrganization !== (selectedUser.organization !== '—' ? selectedUser.organization : '')) {
      if (selectedUser.role === 'teacher') {
        store.updateTeacherDetails(selectedUser.id, {
          schoolName: editUserOrganization.trim()
        });
      }
    }
    // Refresh the selected user row with updated data
    setSelectedUser(prev => prev ? {
      ...prev,
      name: editUserName.trim(),
      email: editUserEmail.trim() || '—',
      organization: editUserOrganization.trim() || '—'
    } : null);
    setIsEditing(false);
  };
  const openEditMode = (row: UserRow) => {
    setSelectedUser(row);
    setEditUserName(row.name);
    setEditUserEmail(row.email !== '—' ? row.email : '');
    setEditUserRole(row.role);
    setEditUserOrganization(row.organization !== '—' ? row.organization : '');
    setIsEditing(true);
  };
  const columns = [{
    key: 'name',
    label: 'Gebruiker',
    render: (_value: any, row: UserRow) => {
      const cfg = ROLE_CONFIG[row.role];
      const Icon = cfg.icon;
      return <div className="flex items-center gap-3" data-id="element-1185">
            <Avatar name={row.name} data-id="element-1186" />
            <div data-id="element-1187">
              <div className="font-medium text-gray-900 text-sm" data-id="element-1188">
                {row.name}
              </div>
              <div className="text-xs text-gray-400" data-id="element-1189">{row.email}</div>
            </div>
          </div>;
    }
  }, {
    key: 'role',
    label: 'Rol',
    render: (_value: any, row: UserRow) => <Badge variant={ROLE_CONFIG[row.role].variant} size="sm" data-id="element-1190">
          {ROLE_CONFIG[row.role].label}
        </Badge>
  }, {
    key: 'organization',
    label: 'Organisatie',
    render: (value: string) => <span className="text-sm text-gray-600 truncate max-w-[140px] block" data-id="element-1191">
          {value}
        </span>
  }, {
    key: 'linkedCount',
    label: 'Koppelingen',
    render: (value: number) => <span className="text-sm text-gray-700" data-id="element-1192">{value > 0 ? value : '—'}</span>
  }, {
    key: 'xp',
    label: 'XP',
    render: (value: number, row: UserRow) => row.role === 'learner' ? <span className="text-sm font-bold text-violet-600" data-id="element-1193">
            {value.toLocaleString('nl-NL')}
          </span> : <span className="text-xs text-gray-400" data-id="element-1194">—</span>
  }, {
    key: 'lastActive',
    label: 'Actief',
    render: (value: string) => <span className="text-xs text-gray-500" data-id="element-1195">{value}</span>
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <Badge variant={value === 'active' ? 'success' : 'neutral'} size="sm" data-id="element-1196">
          {value === 'active' ? 'Actief' : 'Inactief'}
        </Badge>
  }];
  const rowActions = [{
    label: 'Bekijken',
    icon: Eye,
    onClick: (row: UserRow) => {
      setSelectedUser(row);
      setIsEditing(false);
    }
  }, {
    label: 'Bewerken',
    icon: Edit2,
    onClick: openEditMode
  }, ...(onNavigateToLearner ? [{
    label: 'Open dashboard',
    icon: Eye,
    onClick: (row: UserRow) => row.role === 'learner' && onNavigateToLearner(row.id)
  }] : []), {
    label: 'Verwijderen',
    icon: PowerOff,
    onClick: (row: UserRow) => {
      if (window.confirm(`Weet je zeker dat je ${row.name} wilt verwijderen?`)) {
        store.deleteAccount(row.id);
      }
    },
    danger: true
  }];
  return <PageLayout title="Gebruikersbeheer" subtitle="Alle accounts — leerlingen, docenten, ouders en admins" actions={<div className="flex gap-2" data-id="element-1198">
          <Button size="sm" variant="secondary" onClick={() => setIsLinkModalOpen(true)} data-id="element-1199">
            <LinkIcon className="w-4 h-4 mr-1.5" data-id="element-1200" />
            Koppelen
          </Button>
          <Button size="sm" onClick={() => setIsAddModalOpen(true)} data-id="element-1201">
            <Plus className="w-4 h-4 mr-1.5" data-id="element-1202" />
            Gebruiker toevoegen
          </Button>
        </div>} data-id="element-1197">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-1203">
        <StatCard label="Leerlingen" value={counts.learner} icon={<Users className="w-5 h-5" data-id="element-1205" />} color="blue" data-id="element-1204" />
        <StatCard label="Docenten" value={counts.teacher} icon={<GraduationCap className="w-5 h-5" data-id="element-1207" />} color="purple" data-id="element-1206" />
        <StatCard label="Ouders" value={counts.parent} icon={<Heart className="w-5 h-5" data-id="element-1209" />} color="rose" data-id="element-1208" />
        <StatCard label="Admins" value={counts.admin} icon={<Shield className="w-5 h-5" data-id="element-1211" />} color="amber" data-id="element-1210" />
      </div>

      {/* Role filter pills */}
      <div className="flex gap-2 mb-4 flex-wrap" data-id="element-1212">
        {(['all', 'learner', 'teacher', 'parent', 'admin'] as const).map(role => <button key={role} onClick={() => setRoleFilter(role)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${roleFilter === role ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1213">
              {role === 'all' ? `Alle (${users.length})` : role === 'learner' ? `Leerlingen (${counts.learner})` : role === 'teacher' ? `Docenten (${counts.teacher})` : role === 'parent' ? `Ouders (${counts.parent})` : `Admins (${counts.admin})`}
            </button>)}
      </div>

      <DataTable columns={columns} data={filtered} onRowClick={row => {
      setSelectedUser(row);
      setIsEditing(false);
    }} emptyMessage="Geen gebruikers gevonden" rowActions={rowActions} data-id="element-1214" />

      {/* User Detail SlideOver */}
      <SlideOver open={!!selectedUser} onClose={() => {
      setSelectedUser(null);
      setResetConfirm(false);
      setIsEditing(false);
    }} title={isEditing ? 'Gebruiker bewerken' : selectedUser?.name || ''} description={selectedUser && !isEditing ? ROLE_CONFIG[selectedUser.role].label : undefined} data-id="element-1215">
        {selectedUser && <div className="space-y-6" data-id="element-1216">
            {isEditing ? <form onSubmit={handleEditUser} className="space-y-4" data-id="element-1217">
                <div data-id="element-1218">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1219">
                    Naam
                  </label>
                  <input type="text" required value={editUserName} onChange={e => setEditUserName(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1220" />
                </div>

                <div data-id="element-1221">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1222">
                    E-mailadres
                  </label>
                  <input type="email" value={editUserEmail} onChange={e => setEditUserEmail(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1223" />
                </div>

                <div data-id="element-1224">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1225">
                    Rol
                  </label>
                  <select value={editUserRole} onChange={e => setEditUserRole(e.target.value as any)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1226">
                    <option value="learner" data-id="element-1227">Leerling</option>
                    <option value="teacher" data-id="element-1228">Docent</option>
                    <option value="parent" data-id="element-1229">Ouder</option>
                    <option value="admin" data-id="element-1230">Admin</option>
                  </select>
                </div>

                {(editUserRole === 'teacher' || editUserRole === 'learner') && <div data-id="element-1231">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1232">
                      Organisatie / School
                    </label>
                    <input type="text" value={editUserOrganization} onChange={e => setEditUserOrganization(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1233" />
                  </div>}

                <div className="pt-4 flex justify-end gap-2 border-t border-gray-100" data-id="element-1234">
                  <Button type="button" variant="secondary" onClick={() => setIsEditing(false)} data-id="element-1235">
                    Annuleren
                  </Button>
                  <Button type="submit" data-id="element-1236">Opslaan</Button>
                </div>
              </form> : <>
                <div className="grid grid-cols-2 gap-3" data-id="element-1237">
                  <div className="bg-gray-50 rounded-xl p-4" data-id="element-1238">
                    <div className="text-xs text-gray-500 mb-1" data-id="element-1239">Rol</div>
                    <Badge variant={ROLE_CONFIG[selectedUser.role].variant} size="sm" data-id="element-1240">
                      {ROLE_CONFIG[selectedUser.role].label}
                    </Badge>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4" data-id="element-1241">
                    <div className="text-xs text-gray-500 mb-1" data-id="element-1242">
                      Organisatie
                    </div>
                    <div className="font-semibold text-gray-800 text-sm" data-id="element-1243">
                      {selectedUser.organization}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4" data-id="element-1244">
                    <div className="text-xs text-gray-500 mb-1" data-id="element-1245">E-mail</div>
                    <div className="font-semibold text-gray-800 text-sm truncate" data-id="element-1246">
                      {selectedUser.email}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4" data-id="element-1247">
                    <div className="text-xs text-gray-500 mb-1" data-id="element-1248">
                      Laatste activiteit
                    </div>
                    <div className="font-semibold text-gray-800 text-sm" data-id="element-1249">
                      {selectedUser.lastActive}
                    </div>
                  </div>
                  {selectedUser.role === 'learner' && <div className="bg-violet-50 rounded-xl p-4 col-span-2" data-id="element-1250">
                      <div className="text-xs text-gray-500 mb-1" data-id="element-1251">
                        XP Totaal
                      </div>
                      <div className="text-xl font-bold text-violet-600" data-id="element-1252">
                        {selectedUser.xp.toLocaleString('nl-NL')}
                      </div>
                    </div>}
                </div>

                <div className="pt-4 border-t border-gray-100 space-y-2" data-id="element-1253">
                  <div className="flex gap-2" data-id="element-1254">
                    <Button size="sm" variant="secondary" className="flex-1" onClick={() => openEditMode(selectedUser)} data-id="element-1255">
                      <Edit2 className="w-3.5 h-3.5 mr-1.5" data-id="element-1256" /> Bewerken
                    </Button>
                    <Button size="sm" className="flex-1" onClick={() => {
                if (window.confirm(`Weet je zeker dat je ${selectedUser.name} wilt verwijderen?`)) {
                  store.deleteAccount(selectedUser.id);
                  setSelectedUser(null);
                }
              }} data-id="element-1257">
                      <PowerOff className="w-3.5 h-3.5 mr-1.5" data-id="element-1258" /> Verwijderen
                    </Button>
                  </div>

                  {selectedUser.role === 'learner' && onNavigateToLearner && <Button size="sm" variant="secondary" className="w-full" onClick={() => {
              onNavigateToLearner(selectedUser.id);
              setSelectedUser(null);
            }} data-id="element-1259">
                      <Eye className="w-3.5 h-3.5 mr-1.5" data-id="element-1260" /> Dashboard
                    </Button>}

                  {selectedUser.role === 'learner' && <div className="pt-2 border-t border-gray-100" data-id="element-1261">
                      {!resetConfirm ? <button onClick={() => setResetConfirm(true)} className="w-full px-4 py-2.5 text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors" data-id="element-1262">
                          <RotateCcw className="w-3.5 h-3.5 inline mr-1.5" data-id="element-1263" />
                          Voortgang resetten
                        </button> : <div className="p-3 bg-red-50 border border-red-200 rounded-xl space-y-2" data-id="element-1264">
                          <p className="text-xs font-semibold text-red-800" data-id="element-1265">
                            ⚠️ Weet je het zeker? Dit kan niet ongedaan worden
                            gemaakt.
                          </p>
                          <div className="flex gap-2" data-id="element-1266">
                            <button onClick={() => setResetConfirm(false)} className="flex-1 py-2 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-1267">
                              Annuleren
                            </button>
                            <button onClick={() => {
                    store.resetLearnerProgress(selectedUser.id);
                    setResetConfirm(false);
                  }} className="flex-1 py-2 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700" data-id="element-1268">
                              Definitief resetten
                            </button>
                          </div>
                        </div>}
                    </div>}
                </div>
              </>}
          </div>}
      </SlideOver>

      {/* Add User Modal */}
      <SlideOver open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Nieuwe gebruiker" description="Maak een nieuw account aan" data-id="element-1269">
        <form onSubmit={handleAddUser} className="space-y-4" data-id="element-1270">
          <div data-id="element-1271">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1272">
              Rol
            </label>
            <select value={newUserRole} onChange={e => setNewUserRole(e.target.value as any)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1273">
              <option value="learner" data-id="element-1274">Leerling</option>
              <option value="teacher" data-id="element-1275">Docent</option>
              <option value="parent" data-id="element-1276">Ouder</option>
              <option value="admin" data-id="element-1277">Admin</option>
            </select>
          </div>

          <div data-id="element-1278">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1279">
              Naam
            </label>
            <input type="text" required value={newUserName} onChange={e => setNewUserName(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Volledige naam" data-id="element-1280" />
          </div>

          <div data-id="element-1281">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1282">
              E-mailadres (optioneel)
            </label>
            <input type="email" value={newUserEmail} onChange={e => setNewUserEmail(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="email@voorbeeld.nl" data-id="element-1283" />
          </div>

          {newUserRole === 'teacher' && <>
              <div data-id="element-1284">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1285">
                  School
                </label>
                <input type="text" value={newUserSchool} onChange={e => setNewUserSchool(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Naam van de school" data-id="element-1286" />
              </div>
              <div data-id="element-1287">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1288">
                  Klas/Groep
                </label>
                <input type="text" value={newUserClass} onChange={e => setNewUserClass(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Bijv. Groep 7A" data-id="element-1289" />
              </div>
            </>}

          {newUserRole === 'learner' && <div data-id="element-1290">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1291">
                Groep
              </label>
              <select value={newUserGrade} onChange={e => setNewUserGrade(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1292">
                <option value="6" data-id="element-1293">Groep 6</option>
                <option value="7" data-id="element-1294">Groep 7</option>
                <option value="8" data-id="element-1295">Groep 8</option>
              </select>
            </div>}

          <div className="pt-4 flex justify-end gap-2" data-id="element-1296">
            <Button type="button" variant="secondary" onClick={() => setIsAddModalOpen(false)} data-id="element-1297">
              Annuleren
            </Button>
            <Button type="submit" data-id="element-1298">Aanmaken</Button>
          </div>
        </form>
      </SlideOver>

      {/* Link Users Modal */}
      <SlideOver open={isLinkModalOpen} onClose={() => setIsLinkModalOpen(false)} title="Accounts koppelen" description="Koppel een ouder of docent aan een leerling" data-id="element-1299">
        <form onSubmit={handleLinkUsers} className="space-y-4" data-id="element-1300">
          <div data-id="element-1301">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1302">
              Ouder of Docent
            </label>
            <select required value={linkSourceId} onChange={e => setLinkSourceId(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1303">
              <option value="" data-id="element-1304">Selecteer een ouder of docent...</option>
              {users.filter(u => u.role === 'parent' || u.role === 'teacher').map(u => <option key={u.id} value={u.id} data-id="element-1305">
                    {u.name} ({u.role === 'parent' ? 'Ouder' : 'Docent'})
                  </option>)}
            </select>
          </div>

          <div data-id="element-1306">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1307">
              Leerling
            </label>
            <select required value={linkTargetId} onChange={e => setLinkTargetId(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1308">
              <option value="" data-id="element-1309">Selecteer een leerling...</option>
              {users.filter(u => u.role === 'learner').map(u => <option key={u.id} value={u.id} data-id="element-1310">
                    {u.name}
                  </option>)}
            </select>
          </div>

          <div className="pt-4 flex justify-end gap-2" data-id="element-1311">
            <Button type="button" variant="secondary" onClick={() => setIsLinkModalOpen(false)} data-id="element-1312">
              Annuleren
            </Button>
            <Button type="submit" disabled={!linkSourceId || !linkTargetId} data-id="element-1313">
              Koppelen
            </Button>
          </div>
        </form>
      </SlideOver>
    </PageLayout>;
}